import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import * as api from '../../lib/api';
import { buttonClass, fieldClass, mutedClass } from '../uiStyles';

/**
 * One conversation: the messages plus the reply box. `role` is the side the
 * viewer is on, which decides bubble alignment and nothing else — the server
 * still decides who is allowed to write what.
 */
const ThreadView = ({ threadId, role, onChanged }) => {
  const { isDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ status: 'loading', thread: null, messages: [] });
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: 'loading' }));
    try {
      const data = await api.fetchThread(threadId);
      setState({ status: 'ready', thread: data.thread, messages: data.messages });
      // Opening the thread cleared an unread badge server-side; tell the list.
      if (onChanged) onChanged();
    } catch (caught) {
      setState({ status: 'error', thread: null, messages: [], code: caught.code });
    }
  }, [threadId, onChanged]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'nearest' });
  }, [state.messages.length]);

  const send = async (event) => {
    event.preventDefault();
    const body = reply.trim();
    if (!body || sending) return;

    setSending(true);
    setError(null);
    try {
      const data = await api.postMessage(threadId, body);
      setState((current) => ({ ...current, messages: [...current.messages, data.message] }));
      setReply('');
      if (onChanged) onChanged();
    } catch (caught) {
      setError(caught.code || 'server-error');
    } finally {
      setSending(false);
    }
  };

  if (state.status === 'loading') {
    return <p className={`p-6 text-sm ${mutedClass(isDarkMode)}`}>{t('messages.loading')}</p>;
  }

  if (state.status === 'error') {
    return (
      <div className="p-6 space-y-3">
        <p className="text-sm text-red-500">
          {t([`messages.errors.${state.code}`, 'messages.errors.server-error'])}
        </p>
        <button type="button" onClick={load} className={buttonClass(isDarkMode)}>
          {t('messages.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-primary/10' : 'border-gray-200'}`}>
        <h3 className={`font-bold ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>{state.thread.subject}</h3>
        <p className={`text-xs ${mutedClass(isDarkMode)}`}>
          {role === 'admin' ? `${state.thread.user.name} · ${state.thread.user.email} · ` : ''}
          {new Date(state.thread.createdAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {state.messages.map((message) => {
          const mine = message.sender === role;
          return (
            <div key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine
                  ? isDarkMode ? 'bg-primary/15 border border-primary/30' : 'bg-green-50 border border-green-200'
                  : isDarkMode ? 'bg-dark/60 border border-primary/10' : 'bg-gray-100 border border-gray-200'}`}
              >
                <p className={`text-[11px] mb-1 ${mutedClass(isDarkMode)}`}>
                  {message.sender === 'admin' ? t('messages.adminLabel') : message.authorName}
                  {' · '}
                  {new Date(message.createdAt).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}
                </p>
                <p className={`text-sm whitespace-pre-wrap break-words ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>
                  {message.body}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className={`p-3 border-t ${isDarkMode ? 'border-primary/10' : 'border-gray-200'}`}>
        {error && (
          <p className="text-sm text-red-500 mb-2">
            {t([`messages.errors.${error}`, 'messages.errors.server-error'])}
          </p>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            onKeyDown={(event) => {
              // Ctrl/Cmd+Enter sends, plain Enter keeps writing a new line.
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) send(event);
            }}
            rows={2}
            aria-label={t('messages.replyLabel')}
            placeholder={t('messages.replyPlaceholder')}
            className={`${fieldClass(isDarkMode)} resize-y text-sm`}
          />
          <button
            type="submit"
            disabled={sending || !reply.trim()}
            className={`${buttonClass(isDarkMode, 'primary')} h-[42px] inline-flex items-center gap-2`}
          >
            <FaPaperPlane className="rtl:rotate-180" />
            {sending ? t('messages.sending') : t('messages.send')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ThreadView;
